import { lazy, Suspense, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/hooks/useAuth";
import { useAdmin } from "@/hooks/useAdmin";
import { usePartner } from "@/hooks/usePartner";
import { useCoach } from "@/hooks/useCoach";
import { CartProvider } from "@/contexts/CartContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { CartDrawer } from "@/components/cart/CartDrawer";
import { LoadingBolt } from "./components/icons/LightningBolt";
import { TopProgressBar } from "./components/layout/TopProgressBar";
import { RouteFallback } from "./components/layout/RouteFallback";

const Home = lazy(() => import("./pages/Home"));
const Auth = lazy(() => import("./pages/Auth"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const Training = lazy(() => import("./pages/Training"));
const Events = lazy(() => import("./pages/Events"));
const Store = lazy(() => import("./pages/Store"));
const Benefits = lazy(() => import("./pages/Benefits"));
const Profile = lazy(() => import("./pages/Profile"));
const Premium = lazy(() => import("./pages/Premium"));
const Checkout = lazy(() => import("./pages/Checkout"));
const TransferPayment = lazy(() => import("./pages/TransferPayment"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));
const PaymentPending = lazy(() => import("./pages/PaymentPending"));
const PaymentFailure = lazy(() => import("./pages/PaymentFailure"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Rewards = lazy(() => import("./pages/Rewards"));
const PublicProfile = lazy(() => import("./pages/PublicProfile"));
const PartnerVerify = lazy(() => import("./pages/PartnerVerify"));
const Landing = lazy(() => import("./pages/Landing"));
const StravaCallback = lazy(() => import("./pages/StravaCallback"));
const AuthCallback = lazy(() => import("./pages/AuthCallback"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Admin = lazy(() => import("./pages/admin/Admin"));
const Coach = lazy(() => import("./pages/Coach"));
const MiSemana = lazy(() => import("./pages/MiSemana"));
const Habits = lazy(() => import("./pages/Habits"));
const Leaderboard = lazy(() => import("./pages/Leaderboard"));

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 5 * 60 * 1000, gcTime: 10 * 60 * 1000, retry: 1 } },
});

// Public index: Landing for unauthenticated users (Google can index it), Home for logged-in users
const IndexRoute = () => {
  const { user, loading, profile } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-background"><LoadingBolt size={48} /></div>;
  if (!user) return <Landing />;
  if (profile && !profile.onboarding_completed_at) return <Navigate to="/onboarding" replace />;
  return <Home />;
};

// Protected route wrapper
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading, profile } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <LoadingBolt size={48} />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  // Redirect to onboarding if not completed
  if (user && profile && !profile.onboarding_completed_at) {
    return <Navigate to="/onboarding" replace />;
  }

  return <>{children}</>;
};

// Auth route wrapper (redirects if already logged in)
const AuthRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading, profile } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <LoadingBolt size={48} />
      </div>
    );
  }

  if (user && profile?.onboarding_completed_at) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

// Admin route wrapper (verifies admin role)
const AdminRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading: authLoading } = useAuth();
  const { isAdmin, loading: adminLoading } = useAdmin();

  if (authLoading || adminLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <LoadingBolt size={48} />
      </div>
    );
  }

  if (!user || !isAdmin) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};
// Onboarding route wrapper (only checks auth, no onboarding redirect)
const OnboardingRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <LoadingBolt size={48} />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  return <>{children}</>;
};

// Coach route wrapper (verifies coach role)
const CoachRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading: authLoading } = useAuth();
  const { isCoach, loading: coachLoading } = useCoach();
  const { isAdmin, loading: adminLoading } = useAdmin();

  if (authLoading || coachLoading || adminLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <LoadingBolt size={48} />
      </div>
    );
  }

  if (!user || (!isCoach && !isAdmin)) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

// Partner route wrapper (verifies partner role)
const PartnerRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading: authLoading } = useAuth();
  const { isPartner, loading: partnerLoading } = usePartner();

  if (authLoading || partnerLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <LoadingBolt size={48} />
      </div>
    );
  }

  if (!user || !isPartner) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

/**
 * Preload main bottom-nav routes after first paint so tab switches feel instant.
 * Runs in idle time so it doesn't compete with the current page's resources.
 */
const usePreloadMainRoutes = () => {
  useEffect(() => {
    const preload = () => {
      void import("./pages/Home");
      void import("./pages/Training");
      void import("./pages/Events");
      void import("./pages/Store");
      void import("./pages/Benefits");
      void import("./pages/Profile");
    };
    type IdleWindow = Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
    };
    const w = window as IdleWindow;
    if (w.requestIdleCallback) {
      w.requestIdleCallback(preload, { timeout: 2500 });
    } else {
      window.setTimeout(preload, 1500);
    }
  }, []);
};

const AppRoutes = () => {
  usePreloadMainRoutes();
  return (
    <Suspense fallback={<RouteFallback />}>
    <Routes>
      {/* Public routes */}
      <Route path="/landing" element={<Landing />} />
      <Route path="/register" element={<Navigate to="/auth" replace />} />
      <Route
        path="/auth"
        element={
          <AuthRoute>
            <Auth />
          </AuthRoute>
        }
      />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/reset-password" element={<ResetPassword />} />

      {/* Semi-protected: needs auth but not profile completion */}
      <Route
        path="/onboarding"
        element={
          <OnboardingRoute>
            <Onboarding />
          </OnboardingRoute>
        }
      />

      {/* Index: Landing for guests (SEO), Home for authenticated users */}
      <Route path="/" element={<IndexRoute />} />
      <Route path="/training" element={<ProtectedRoute><Training /></ProtectedRoute>} />
      <Route path="/events" element={<ProtectedRoute><Events /></ProtectedRoute>} />
      <Route path="/store" element={<ProtectedRoute><Store /></ProtectedRoute>} />
      <Route path="/benefits" element={<ProtectedRoute><Benefits /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
      <Route path="/premium" element={<ProtectedRoute><Premium /></ProtectedRoute>} />
      <Route path="/checkout" element={<ProtectedRoute><Checkout /></ProtectedRoute>} />
      <Route path="/transfer/:orderId" element={<ProtectedRoute><TransferPayment /></ProtectedRoute>} />
      <Route path="/payment/success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
      <Route path="/payment/pending" element={<ProtectedRoute><PaymentPending /></ProtectedRoute>} />
      <Route path="/payment/failure" element={<ProtectedRoute><PaymentFailure /></ProtectedRoute>} />
      <Route path="/recompensas" element={<ProtectedRoute><Rewards /></ProtectedRoute>} />
      <Route path="/mi-semana" element={<ProtectedRoute><MiSemana /></ProtectedRoute>} />
      <Route path="/habitos" element={<ProtectedRoute><Habits /></ProtectedRoute>} />
      <Route path="/ranerzzz" element={<ProtectedRoute><Leaderboard /></ProtectedRoute>} />
      <Route path="/runner/:userId" element={<ProtectedRoute><PublicProfile /></ProtectedRoute>} />
      <Route path="/strava/callback" element={<ProtectedRoute><StravaCallback /></ProtectedRoute>} />
      <Route path="/privacy" element={<Privacy />} />

      {/* Partner route */}
      <Route
        path="/partner-verify"
        element={
          <PartnerRoute>
            <PartnerVerify />
          </PartnerRoute>
        }
      />

      <Route
        path="/admin"
        element={
          <AdminRoute>
            <Admin />
          </AdminRoute>
        }
      />

      <Route
        path="/coach"
        element={
          <CoachRoute>
            <Coach />
          </CoachRoute>
        }
      />

      {/* 404 */}
      <Route path="*" element={<NotFound />} />
    </Routes>
    </Suspense>
  );
};

const App = () => (
  <ThemeProvider>
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <CartProvider>
              <TopProgressBar />
              <AppRoutes />
              <CartDrawer />
          </CartProvider>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
  </ThemeProvider>
);

export default App;
